/**
 * @fileoverview Popup Timer Module
 *
 * This module provides a timer implementation for the popup logic system.
 * It allows for precise timing control with the ability to start, stop, and reset
 * a timer that triggers a callback function when a threshold is reached.
 *
 * @module popup-logic/timer
 */

/**
 * Class representing a timer for popup management.
 * This timer counts up to a threshold and then executes a callback function.
 * It can be reset, started, and stopped as needed, making it ideal for
 * implementing popup display logic that needs to reset on user interaction.
 * Uses high-resolution timing with performance.now() and requestAnimationFrame
 * for accurate millisecond counting.
 */
export class PopupTimer {
  /** @private Threshold in milliseconds at which the callback will be triggered */
  private triggerThreshold: number;

  /** @private ID of the current animation frame request or null if not running */
  private animationFrameId: number | null;

  /** @private Current status of the timer: "stopped" or "running" */
  private status: 'stopped' | 'running';

  /** @private Current elapsed time in milliseconds since last reset */
  private elapsedTime: number;

  /** @private Timestamp of the last update for calculating elapsed time */
  private lastTimestamp: number;

  /** @private Callback function to execute when the threshold is reached */
  private callback: Function | undefined;

  /** @private Accumulated time drift for compensation */
  private driftCompensation: number;

  /**
   * Creates a new PopupTimer instance
   * @param {Object} options - Configuration options for the timer
   * @param {number} [options.triggerThreshold=15000] - Time in milliseconds before triggering the callback
   * @param {Function} [options.callback] - Function to call when the timer reaches the threshold
   */
  constructor({ triggerThreshold, callback }: { triggerThreshold?: number; callback?: Function }) {
    this.triggerThreshold = triggerThreshold ?? 15000;
    this.callback = callback;
    this.animationFrameId = null;
    this.elapsedTime = 0;
    this.lastTimestamp = 0;
    this.status = 'stopped';
    this.driftCompensation = 0;
  }

  /**
   * Resets the timer to zero without stopping it
   * This is useful when you want to restart the countdown without
   * stopping and restarting the animation frame
   */
  reset(): void {
    this.elapsedTime = 0;
    this.lastTimestamp = performance.now();
    this.driftCompensation = 0;
  }

  /**
   * Animation frame callback that updates the timer
   * @private
   * @param {number} timestamp - High resolution timestamp from requestAnimationFrame
   */
  private update(timestamp: number): void {
    // Calculate time elapsed since last frame
    const deltaTime = timestamp - this.lastTimestamp;
    this.lastTimestamp = timestamp;

    // Add the elapsed time to our counter
    this.elapsedTime += deltaTime;

    // Check if we've reached the threshold
    if (this.elapsedTime >= this.triggerThreshold) {
      // Calculate how much we overshot by
      this.driftCompensation = this.elapsedTime - this.triggerThreshold;

      // Execute callback
      this.callback?.();

      // Reset timer, accounting for drift compensation
      this.elapsedTime = 0;

      // If the drift is too large, just reset it to avoid strange behavior
      if (this.driftCompensation > 100) {
        this.driftCompensation = 0;
      }
    }

    // Continue the animation loop if we're still running
    if (this.status === 'running') {
      this.animationFrameId = requestAnimationFrame(this.update.bind(this));
    }
  }

  /**
   * Starts the timer or resets it if already running
   * Uses requestAnimationFrame for more accurate timing than setInterval
   */
  start(): void {
    // If already running, just reset the elapsed time
    if (this.status === 'running') {
      this.reset();
      return;
    }

    // Set initial timestamp and status
    this.lastTimestamp = performance.now();
    this.status = 'running';

    // Start the animation frame loop
    this.animationFrameId = requestAnimationFrame(this.update.bind(this));
  }

  /**
   * Stops the timer if it's currently running
   * This will cancel the animation frame request
   */
  stop(): void {
    if (this.status !== 'running') return;

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    this.status = 'stopped';
  }

  /**
   * Gets the current elapsed time in milliseconds since last reset
   * @returns {number} Current elapsed time
   */
  getCurrentTime(): number {
    return this.elapsedTime;
  }

  /**
   * Gets the current status of the timer
   * @returns {"stopped" | "running"} Current timer status
   */
  getStatus(): 'stopped' | 'running' {
    return this.status;
  }

  /**
   * Gets the percentage of time elapsed relative to the threshold
   * @returns {number} Percentage from 0 to 100
   */
  getPercentComplete(): number {
    return Math.min(100, (this.elapsedTime / this.triggerThreshold) * 100);
  }

  /**
   * Gets the remaining time until the threshold is reached
   * @returns {number} Remaining time in milliseconds
   */
  getRemainingTime(): number {
    return Math.max(0, this.triggerThreshold - this.elapsedTime);
  }
}
